import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AREAS } from "@/lib/site";
import { AREA_PAGES } from "@/content/areas";
import { Seo } from "@/components/Seo";
import { breadcrumbJsonLd } from "@/components/Breadcrumb";
import { PageHero } from "@/components/PageHero";
import { MapEmbed } from "@/components/MapEmbed";
import { ImageSlot } from "@/components/ImageSlot";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/home/CtaBand";

const BREADCRUMB = [
  { label: "Home", to: "/" },
  { label: "Areas", to: "/areas" },
];

export default function AreasIndex() {
  return (
    <div data-testid="areas-index-page">
      <Seo
        title="Areas We Cover | DMS Home Improvements"
        description="Family-run builders and plasterers covering Molesey, East and West Molesey, Kingston upon Thames, Surbiton, Thames Ditton, Esher, Cobham and the surrounding area of Surrey and South West London."
        jsonLd={breadcrumbJsonLd(BREADCRUMB)}
      />
      <PageHero
        eyebrow="Surrey & South West London"
        h1="Areas We Cover"
        intro="We are based in Molesey and work throughout the surrounding towns and villages. Wherever you are on the map below, you get the same family-run team, the same free site visit and the same fixed written quote."
        breadcrumb={BREADCRUMB}
      />

      <section data-testid="areas-map-section" className="bg-white pb-16 md:pb-20">
        <div className="container-site">
          <Reveal>
            <MapEmbed title="Map of the areas DMS Home Improvements covers, centred on Molesey" query="Molesey, Surrey, UK" zoom={12} className="h-[360px] md:h-[460px]" />
          </Reveal>
        </div>
      </section>

      <section data-testid="areas-grid-section" className="section section-alt">
        <div className="container-site">
          <Reveal className="max-w-[720px]">
            <h2>Choose Your Area</h2>
            <p className="sub-line mt-4 text-body">Pick your town for local detail on the building and plastering work we do there.</p>
          </Reveal>
          <ul className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-8">
            {AREAS.map((a, i) => {
              const area = AREA_PAGES.find((p) => p.id === a.id);
              const photo = area?.photos?.[0];
              return (
                <Reveal as="li" key={a.id} delay={i * 60}>
                  <Link
                    to={a.to}
                    data-testid={`areas-card-${a.id}`}
                    className="card group flex h-full flex-col overflow-hidden transition-[border-color] duration-200 hover:border-brand"
                  >
                    <ImageSlot label={photo?.id || `builders-${a.id}.webp`} note={photo?.note || `PHOTO NEEDED: a project in ${a.name}, landscape, min 1600px wide`} ratio="4:3" alt={photo?.alt} />
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="text-2xl">{a.name}</h3>
                      <span className="mt-4 inline-flex items-center gap-2 text-[15px] font-semibold text-brand">
                        Builders in {a.name}
                        <ArrowRight size={18} aria-hidden="true" className="transition-transform duration-200 ease-out group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
